/**
 * Events Management Page
 *
 * Reason: Allows admins to view and manage events in their organization,
 * including creating events and assigning users to events
 */

import { FC, useState, useContext, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useDebouncedCallback } from 'use-debounce';
import { useNavigate } from 'react-router-dom';

import { UserContext } from '@/contexts/UserContext';
import { useListEvents, Event } from '@/adapters/events-v2';
import useAuthorization from '@/hooks/useAuthorization';
import PrimaryButton from '@/components/common/PrimaryButton';
import DefaultButton from '@/components/common/DefaultButton';
import Loading from '@/components/Loading';
import * as urls from '@/routePaths';

import styles from './Events.module.scss';
import CreateEventDialog from './CreateEventDialog';
import EditEventDialog from './EditEventDialog';
import ManageEventMembersDialog from './ManageEventMembersDialog';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid var(--silver)`,
  borderRadius: '8px',
  marginBottom: '15px',
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(({ theme }) => ({
  backgroundColor: 'var(--gallery)',
  borderBottomColor: 'transparent',
  borderRadius: '8px',
  flexDirection: 'row-reverse',
  padding: 0,
  '&.Mui-expanded': {
    borderRadius: '8px 8px 0 0',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    padding: '10px',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: '16px',
  borderTop: '1px solid var(--silver)',
}));

// Stable empty array to prevent infinite re-renders
const EMPTY_EVENTS: Event[] = [];

const Events: FC = () => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [managingEvent, setManagingEvent] = useState<Event | null>(null);
  const navigate = useNavigate();

  const { userState } = useContext(UserContext);
  const { hasRole } = useAuthorization();

  const { data: events = EMPTY_EVENTS, isLoading, refetch } = useListEvents(userState?.orgId);

  // Check if user is admin
  const isAdmin = hasRole(['admin', 'super_admin']);

  /**
   * Filters events based on search query
   *
   * @reason Enables searching events by name or description for easier management
   */
  const debouncedSearch = useDebouncedCallback((value: string) => {
    if (!value.trim()) {
      setFilteredEvents(events);
      return;
    }

    const searchLower = value.toLowerCase();
    const filtered = events.filter(
      (event) =>
        event.name.toLowerCase().includes(searchLower) ||
        event.description?.toLowerCase().includes(searchLower),
    );
    setFilteredEvents(filtered);
  }, 300);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleCreateEvent = (): void => {
    setIsCreateDialogOpen(true);
  };

  const handleEditEvent = (event: Event): void => {
    setEditingEvent(event);
  };

  const handleManageMembers = (event: Event): void => {
    setManagingEvent(event);
  };

  const handleDialogClose = (): void => {
    setIsCreateDialogOpen(false);
    setEditingEvent(null);
    setManagingEvent(null);
    void refetch();
  };

  // Update filtered events when events data changes
  useEffect(() => {
    setFilteredEvents(events);
  }, [events]);

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography>Only admins can manage events.</Typography>
      </div>
    );
  }

  const displayEvents = searchValue ? filteredEvents : events;

  return (
    <>
      <div className={styles.navbar}>
        <h1 className={styles.projectsHeadTitle}>Events</h1>
        <div className={styles.searchBar}>
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search by name or description"
          />
          <SearchIcon sx={{ color: '#7a7a7a', position: 'absolute', right: '16px', top: '7px' }} />
        </div>

        <PrimaryButton onClick={handleCreateEvent}>
          <div className={styles.buttonLabel} style={{ display: 'flex', alignItems: 'center' }}>
            Create Event
          </div>
        </PrimaryButton>
      </div>

      {isLoading ? (
        <Loading />
      ) : displayEvents.length === 0 ? (
        <Box sx={{ textAlign: 'center', marginTop: 4 }}>
          <Typography variant="h6">
            {searchValue ? 'No events found' : 'No events in organization'}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Create an event to get started
          </Typography>
        </Box>
      ) : (
        <div className={styles.table}>
          <div className={styles.tableTitle}>
            <div className={styles.tableItem}>Name</div>
            <div className={styles.tableItem}>Description</div>
            <div className={styles.tableItem}>Status</div>
            <div className={styles.tableItem}>Actions</div>
          </div>

          {displayEvents.map((event) => (
            <Accordion key={event.id}>
              <AccordionSummary>
                <Typography
                  sx={{ flex: '2 1', padding: 0 }}
                  className={styles.tableItem + ' ' + styles.specialColumn}
                >
                  {event.name}
                </Typography>
                <Typography sx={{ flex: '2 1', padding: 0 }} className={styles.specialColumn}>
                  {event.description || '-'}
                </Typography>
                <Box sx={{ flex: '1 1', padding: 0 }} className={styles.specialColumn}>
                  <Chip
                    label={event.isActive ? 'Active' : 'Inactive'}
                    color={event.isActive ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Box
                  sx={{ flex: '2 1', padding: 0, display: 'flex', gap: 1 }}
                  className={styles.tableBtn}
                >
                  <DefaultButton
                    size="small"
                    sx={{ paddingLeft: 1, paddingRight: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManageMembers(event);
                    }}
                  >
                    Manage Users
                  </DefaultButton>
                  <DefaultButton
                    size="small"
                    sx={{ paddingLeft: 1, paddingRight: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditEvent(event);
                    }}
                  >
                    Edit
                  </DefaultButton>
                  <DefaultButton
                    size="small"
                    sx={{ paddingLeft: 1, paddingRight: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`${urls.BRAND_KIT_URL}/${event.id}`);
                    }}
                  >
                    Brand Kit
                  </DefaultButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Event ID: {event.id}
                  </Typography>
                  {event.startDate && (
                    <Typography variant="body2" color="textSecondary">
                      Start Date: {new Date(event.startDate).toLocaleDateString()}
                    </Typography>
                  )}
                  {event.endDate && (
                    <Typography variant="body2" color="textSecondary">
                      End Date: {new Date(event.endDate).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary">
                    Created: {new Date(event.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          <div className={styles.tableTitle}>
            <div className={styles.tableItem}>Name</div>
            <div className={styles.tableItem}>Description</div>
            <div className={styles.tableItem}>Status</div>
            <div className={styles.tableItem}>Actions</div>
          </div>
        </div>
      )}

      {isCreateDialogOpen && (
        <CreateEventDialog open={isCreateDialogOpen} onClose={handleDialogClose} />
      )}
      {editingEvent && (
        <EditEventDialog open={!!editingEvent} event={editingEvent} onClose={handleDialogClose} />
      )}
      {managingEvent && (
        <ManageEventMembersDialog
          open={!!managingEvent}
          event={managingEvent}
          onClose={handleDialogClose}
        />
      )}
    </>
  );
};

export default Events;
